import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BrandCard, type BrandDetails } from '../components/brands/BrandCard';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { WebsiteFetchModal } from '../components/website/WebsiteFetchModal';
import { useBrands } from '../hooks/useBrands';
import { useBrandDelete } from '../hooks/useBrandDelete';
import { auth } from '../config/firebase';

interface BrandsData {
  brands: BrandDetails[];
}

const Brands = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [brandToDelete, setBrandToDelete] = useState<BrandDetails | null>(null);
  const [isWebsiteFetchModalOpen, setIsWebsiteFetchModalOpen] = useState(false);

  useEffect(() => {
    document.title = t('brands.title', 'Brands');
  }, [t]);

  const { data, isLoading, error, refetch } = useBrands();
  const brandDeleteMutation = useBrandDelete();
  const brands = (data as BrandsData)?.brands || [];

  const filteredBrands = useMemo(() => brands.filter((brand: BrandDetails) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      brand.name.toLowerCase().includes(searchLower) ||
      brand.description.toLowerCase().includes(searchLower) ||
      brand.keywords.some((keyword: string) => keyword.toLowerCase().includes(searchLower))
    );
  }), [brands, searchQuery]);

  const handleAcceptInvite = (brandId: string) => {
    console.log('Accepting invite for brand:', brandId);
    // Here you would typically make an API call to accept the invitation
  };

  const handleDeclineInvite = (brandId: string) => {
    console.log('Declining invite for brand:', brandId);
    // Here you would typically make an API call to decline the invitation
  };

  const handleDelete = async (brandId: string) => {
    try {
      // Use the mutation directly
      await brandDeleteMutation.mutateAsync(brandId);
      // Explicitly refetch after successful deletion
      await refetch();
      setBrandToDelete(null);
    } catch (error) {
      console.error('Error deleting brand:', error);
      alert('Failed to delete brand. Please try again.');
    }
  };

  const handleAddBrand = () => {
    navigate('/brand-strategy-lab/new');
  };

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      <div className="w-[95%] mx-auto">
        <div className="py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 font-title mb-2">
                {t('brands.title')}
              </h1>
              <p className="mt-1 text-gray-500 font-body">
                {t('brands.subtitle')}
              </p>
            </div>
            <button 
              onClick={() => setIsWebsiteFetchModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('brands.addBrand')}
            </button>
          </div>

          <div className="mt-6 relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('brands.search')}
              className="block w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className={`grid grid-cols-1 gap-6 pb-8 ${
          filteredBrands.length === 1 ? 'lg:grid-cols-1 lg:max-w-md lg:mx-auto' :
          filteredBrands.length === 2 ? 'lg:grid-cols-2' :
          'lg:grid-cols-2 xl:grid-cols-3'
        }`}>
          {isLoading && (
            <>
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-white rounded-xl border border-black shadow-lg p-6 animate-pulse">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                      <div>
                        <div className="h-6 w-32 bg-gray-200 rounded mb-2"></div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                          <div className="h-4 w-24 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                        <div className="h-4 w-8 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
                    </div>
                  </div>
                  <div className="h-10 w-3/4 bg-gray-200 rounded mb-6"></div>
                  <div className="space-y-4">
                    <div>
                      <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-6 w-24 bg-gray-200 rounded-lg"></div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2].map((i) => (
                          <div key={i} className="h-6 w-32 bg-gray-200 rounded-lg"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          
          {error && (
            <div className="col-span-full text-center py-12">
              <p className="text-red-500">Failed to load brands. Please try again later.</p>
            </div>
          )}
          
          {filteredBrands.length > 0 ? (
            filteredBrands.map((brand) => (
              <BrandCard 
                key={brand.id} 
                brand={brand}
                onDelete={() => setBrandToDelete(brand)}
                onAcceptInvite={handleAcceptInvite}
                onDeclineInvite={handleDeclineInvite}
              />
            ))
          ) : !isLoading && brands.length === 0 ? (
            // Empty state for no brands at all
            <div className="col-span-full flex flex-col items-center justify-center py-16">
              <p className="text-gray-500 text-center mb-6">
                {t('brands.noBrands', 'Aucune marque créée pour l\'instant. Créez votre première marque pour commencer.')}
              </p>
              <button
                onClick={() => setIsWebsiteFetchModalOpen(true)}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('brands.createFirst', 'Créer votre première Marque')}
              </button>
            </div>
          ) : (
            // No results from search
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500">{t('brands.noResults')}</p>
            </div>
          )}
        </div>
      </div>
      
      <DeleteConfirmationModal
        isOpen={brandToDelete !== null}
        onClose={() => setBrandToDelete(null)}
        onConfirm={() => {
          if (brandToDelete) {
            handleDelete(brandToDelete.id);
          }
        }}
        title={t('brands.delete.title')}
        message={`Êtes-vous sûr de vouloir supprimer la marque "<strong><em>${brandToDelete?.name || ''}</em></strong>" ? Cette action ne peut pas être annulée.`}
        warning={t('brands.delete.warning')}
      />
      
      <WebsiteFetchModal
        isOpen={isWebsiteFetchModalOpen}
        onClose={() => setIsWebsiteFetchModalOpen(false)}
      />
    </div>
  );
};

export default Brands;